// Static tax-rate lookup used to auto-suggest a sales-tax / VAT receipt adjustment
// during onboarding. This is intentionally a baked-in table (no external API): it
// gets the business to an approximately-correct rate at zero cost, and the owner
// confirms it and can edit or remove it anytime in Settings.
//
// IMPORTANT - these are SUGGESTIONS, not tax advice:
//  - US values are the STATE BASE rate only. They do NOT include county/city
//    add-ons, so the real combined rate a business owes is often higher. The
//    owner is expected to adjust it. (A future version may integrate a ZIP-level
//    tax API for the exact combined rate.)
//  - Country values are the standard national VAT/GST rate. Reduced/zero-rated
//    categories and surcharges are not modelled.
//  - Rates change. Review periodically.

// 50 states + DC, for the constrained Province/State dropdown shown when Country = US.
export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

// Canadian provinces + territories, for the constrained dropdown shown when Country = CA.
export const CA_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

// US state base sales-tax rate (%). The five "NOMAD" states with no statewide
// sales tax (AK, DE, MT, NH, OR) are 0 -> no suggestion is offered for them.
const US_STATE_TAX = {
  AL: 4.0,   AK: 0,     AZ: 5.6,   AR: 6.5,   CA: 7.25,  CO: 2.9,
  CT: 6.35,  DE: 0,     DC: 6.0,   FL: 6.0,   GA: 4.0,   HI: 4.0,
  ID: 6.0,   IL: 6.25,  IN: 7.0,   IA: 6.0,   KS: 6.5,   KY: 6.0,
  LA: 5.0,   ME: 5.5,   MD: 6.0,   MA: 6.25,  MI: 6.0,   MN: 6.875,
  MS: 7.0,   MO: 4.225, MT: 0,     NE: 5.5,   NV: 6.85,  NH: 0,
  NJ: 6.625, NM: 4.875, NY: 4.0,   NC: 4.75,  ND: 5.0,   OH: 5.75,
  OK: 4.5,   OR: 0,     PA: 6.0,   RI: 7.0,   SC: 6.0,   SD: 4.2,
  TN: 7.0,   TX: 6.25,  UT: 6.1,   VT: 6.0,   VA: 5.3,   WA: 6.5,
  WV: 6.0,   WI: 5.0,   WY: 4.0,
};

// Canadian combined sales-tax rate (%) by province/territory: GST (or HST) plus any
// provincial portion (PST/QST). These are the standard combined rates as a single
// receipt line. Alberta + the territories are GST-only (5%).
const CA_PROVINCE_TAX = {
  AB: 5.0,   BC: 12.0,  MB: 12.0,  NB: 15.0,  NL: 15.0,  NS: 14.0,
  NT: 5.0,   NU: 5.0,   ON: 13.0,  PE: 15.0,  QC: 14.975, SK: 11.0,
  YT: 5.0,
};

// Countries whose tax rate varies by sub-region -> onboarding shows a constrained
// region dropdown and getTaxSuggestion drills into the region. Everything else uses
// the single national rate in COUNTRY_TAX below.
const SUBREGION_COUNTRIES = {
  US: { regions: US_STATES,    rates: US_STATE_TAX,    noun: 'sales tax' },
  CA: { regions: CA_PROVINCES, rates: CA_PROVINCE_TAX, noun: 'sales tax' },
};

// National VAT/GST standard rate by country (matches the onboarding country list).
// `label` is the local tax name and is used both for the adjustment name and the
// confirmation copy. Countries we don't have a clean single rate for (e.g. Brazil)
// and zero-VAT places (Hong Kong) are omitted -> no suggestion.
const COUNTRY_TAX = {
  GB: { rate: 20.0, label: 'VAT' },
  AU: { rate: 10.0, label: 'GST' },
  NZ: { rate: 15.0, label: 'GST' },
  PH: { rate: 12.0, label: 'VAT' },
  SG: { rate: 9.0,  label: 'GST' },
  MY: { rate: 6.0,  label: 'SST' },
  ID: { rate: 11.0, label: 'VAT' },
  TH: { rate: 7.0,  label: 'VAT' },
  VN: { rate: 10.0, label: 'VAT' },
  IN: { rate: 18.0, label: 'GST' },
  JP: { rate: 10.0, label: 'Consumption Tax' },
  KR: { rate: 10.0, label: 'VAT' },
  CN: { rate: 13.0, label: 'VAT' },
  TW: { rate: 5.0,  label: 'VAT' },
  AE: { rate: 5.0,  label: 'VAT' },
  SA: { rate: 15.0, label: 'VAT' },
  NG: { rate: 7.5,  label: 'VAT' },
  GH: { rate: 15.0, label: 'VAT' },
  KE: { rate: 16.0, label: 'VAT' },
  ZA: { rate: 15.0, label: 'VAT' },
  MX: { rate: 16.0, label: 'IVA' },
  AR: { rate: 21.0, label: 'IVA' },
  CO: { rate: 19.0, label: 'IVA' },
  DE: { rate: 19.0, label: 'VAT' },
  FR: { rate: 20.0, label: 'VAT' },
  ES: { rate: 21.0, label: 'VAT' },
  IT: { rate: 22.0, label: 'VAT' },
  NL: { rate: 21.0, label: 'VAT' },
  SE: { rate: 25.0, label: 'VAT' },
  NO: { rate: 25.0, label: 'VAT' },
  DK: { rate: 25.0, label: 'VAT' },
};

// The region list for a country's Province/State dropdown, or null if the country
// uses a single national rate (free-text Province/State is fine then).
export const regionsForCountry = function (country) {
  const cc = String(country ?? '').toUpperCase();
  return SUBREGION_COUNTRIES[cc]?.regions ?? null;
};

// Resolve a region value (code OR full name) to its code within a region list.
const _resolveRegionCode = function (regions, value) {
  if (!value) return null;
  const v = String(value).trim();
  const upper = v.toUpperCase();
  if (regions.some((r) => r.code === upper)) return upper; // already a code
  const match = regions.find((r) => r.name.toLowerCase() === v.toLowerCase());
  return match ? match.code : null;
};

// Which Tax Settings option fits the business location, for the "Recommended"
// badge: { mode, label } or null when we can't tell. US/CA shelf prices are
// before tax (except the no-sales-tax states, e.g. Oregon); VAT/GST countries
// usually show prices with tax included. A suggestion only - never auto-applied.
export const getRecommendedTaxMode = function ({ country, province } = {}) {
  if (!country) return null;
  const cc = String(country).toUpperCase();
  const sub = SUBREGION_COUNTRIES[cc];
  if (sub) {
    const code = _resolveRegionCode(sub.regions, province);
    const regionName = sub.regions.find((r) => r.code === code)?.name;
    if (code && !sub.rates[code]) return { mode: 'none', label: `Recommended for ${regionName}` };
    return { mode: 'exclusive', label: regionName ? `Recommended for ${regionName}` : 'Recommended' };
  }
  if (COUNTRY_TAX[cc]?.rate) return { mode: 'inclusive', label: 'Recommended' };
  return null;
};

// Given the onboarding country + province, return a tax suggestion or null.
// Returns: { name, label, rate } where
//   name  -> adjustment name written to the DB (e.g. "Sales Tax", "VAT")
//   label -> human phrase for the confirm prompt (e.g. "California sales tax")
//   rate  -> percentage number (e.g. 7.25)
export const getTaxSuggestion = function ({ country, province } = {}) {
  if (!country) return null;
  const cc = String(country).toUpperCase();

  // Countries that vary by sub-region (US states, CA provinces) drill into the region.
  const sub = SUBREGION_COUNTRIES[cc];
  if (sub) {
    const code = _resolveRegionCode(sub.regions, province);
    if (!code) return null;
    const rate = sub.rates[code];
    if (!rate) return null; // 0% / no sub-region tax
    const regionName = sub.regions.find((r) => r.code === code)?.name ?? code;
    return { name: 'Sales Tax', label: `${regionName} ${sub.noun}`, rate };
  }

  const entry = COUNTRY_TAX[cc];
  if (!entry || !entry.rate) return null;
  return { name: entry.label, label: entry.label, rate: entry.rate };
};
